import React, { useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ChevronDown,
  Rocket,
  Film,
  Image as ImageIcon,
  GalleryHorizontal,
  Compass,
  Clapperboard,
  Scissors,
} from "lucide-react";

const SERVICE_ICON_MAP = {
  premium_short: Clapperboard,
  volume_short: Scissors,
  long_form: Film,
  thumbnails: ImageIcon,
  carousels: GalleryHorizontal,
  strategy: Compass,
};

// Expanded content per service: a concise "What is X?" explainer + a
// site-styled visual. Keyed by the service id returned from the backend.
const SERVICE_DETAILS = {
  premium_short: {
    heading: "What is premium short-form editing?",
    body: "Premium short-form editing turns your best moments into scroll-stopping vertical videos — engineered hook-first for watch time, retention, and shares across TikTok, Reels, and YouTube Shorts.",
    image:
      "https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  },
  volume_short: {
    heading: "What is clipping?",
    body: "Clipping turns long-form recordings such as podcasts, interviews, livestreams, and YouTube videos into shorter, engaging pieces of content designed for platforms like TikTok, Instagram Reels, and YouTube Shorts.",
    image:
      "https://images.unsplash.com/photo-1590602847861-f357a9332bbc?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  },
  long_form: {
    heading: "What is long-form editing?",
    body: "Send us a raw recording and receive a fully polished long-form YouTube video — story-driven pacing, layered B-roll, sound design, and color, edited to keep viewers watching to the end.",
    image:
      "https://images.unsplash.com/photo-1618329027137-a520b57c6606?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  },
  thumbnails: {
    heading: "What is thumbnail design?",
    body: "Bold, high-contrast thumbnails built and tested for maximum click-through — the single biggest lever on whether your video gets watched at all.",
    image:
      "https://images.unsplash.com/photo-1626785774625-ddcddc3445e9?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  },
  carousels: {
    heading: "What are carousels?",
    body: "Scroll-stopping Instagram and TikTok carousels designed to earn saves, shares, and follows — from a strong hook slide through to a clear call-to-action.",
    image:
      "https://images.unsplash.com/photo-1724862936518-ae7fcfc052c1?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  },
  strategy: {
    heading: "What is strategy & management?",
    body: "A trained social media manager on-call — content planning, hook optimization, growth consulting, and posting cadence tailored to your niche and goals.",
    image:
      "https://images.unsplash.com/photo-1687125114671-d45714b4631d?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  },
};

const ServiceCard = ({ svc, isOpen, onToggle, reduceMotion }) => {
  const Icon = SERVICE_ICON_MAP[svc.id] || Rocket;
  const details = SERVICE_DETAILS[svc.id] || {
    heading: `What is ${svc.name}?`,
    body: svc.description,
    image: null,
  };
  const panelId = `service-panel-${svc.id}`;
  const btnId = `service-toggle-${svc.id}`;

  const anim = reduceMotion
    ? { initial: false, animate: {}, exit: {}, transition: { duration: 0 } }
    : {
        initial: { height: 0, opacity: 0 },
        animate: { height: "auto", opacity: 1 },
        exit: { height: 0, opacity: 0 },
        transition: { duration: 0.28, ease: [0.22, 1, 0.36, 1] },
      };

  return (
    <div
      className={`squircle border bg-[#111] btn-anim overflow-hidden self-start transition-colors duration-200 ${
        isOpen ? "border-[#3a3a3a]" : "border-[#2b2b2b] hover:border-[#3a3a3a]"
      }`}
      data-testid={`service-card-${svc.id}`}
    >
      <button
        type="button"
        id={btnId}
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={() => onToggle(svc.id)}
        data-testid={`service-toggle-${svc.id}`}
        className="w-full flex items-center gap-4 p-5 sm:p-6 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#6589ff]/60 rounded-[inherit]"
      >
        <span className="h-11 w-11 shrink-0 squircle-sm bg-[#0d0d0d] border border-[#2b2b2b] flex items-center justify-center">
          <Icon size={18} />
        </span>
        <span className="text-lg font-semibold tracking-tight leading-tight flex-1 min-w-0">
          {svc.name}
        </span>
        <ChevronDown
          size={20}
          aria-hidden="true"
          className={`shrink-0 text-[#a0a0a0] transition-transform duration-300 ${
            isOpen ? "rotate-180 text-white" : ""
          }`}
        />
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            key="content"
            id={panelId}
            role="region"
            aria-labelledby={btnId}
            data-testid={`service-content-${svc.id}`}
            {...anim}
            className="overflow-hidden"
          >
            {/* Divider spans the card width, aligned with the header padding */}
            <div className="mx-5 sm:mx-6 border-t border-[#242424]" />

            {/* Dedicated padded content area — ~24–32px on all sides so nothing
                inside (especially the image) can touch the card border */}
            <div className="p-6 sm:p-7 md:p-8">
              <div className="flex flex-col md:flex-row md:items-center gap-7 md:gap-10">
                {/* LEFT — explanatory text (55–60%) */}
                <div className="md:basis-0 md:grow-[56] min-w-0 flex flex-col">
                  <div className="text-sm font-semibold tracking-tight text-white mb-2">
                    {details.heading}
                  </div>
                  <p className="text-sm text-[#a0a0a0] leading-relaxed">
                    {details.body}
                  </p>
                  <div className="mt-5 flex items-center gap-4">
                    <span className="text-xs text-[#7a7a7a]">
                      {(svc.styles || []).length} styles
                    </span>
                    <Link
                      to="/order"
                      data-testid={`service-cta-${svc.id}`}
                      className="text-sm text-white hover:text-[#6589ff] btn-anim inline-flex items-center gap-1.5"
                    >
                      Start with this <ArrowRight size={13} />
                    </Link>
                  </div>
                </div>

                {/* RIGHT — visual (40–45%), centered inside the padded area */}
                {details.image && (
                  <div className="md:basis-0 md:grow-[44] min-w-0 flex items-center justify-center">
                    <div className="squircle-sm overflow-hidden border border-[#2b2b2b] bg-[#0d0d0d] aspect-square w-full max-w-[280px]">
                      <img
                        src={details.image}
                        alt={`${svc.name} — visual example`}
                        loading="lazy"
                        className="h-full w-full object-cover opacity-90"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const ServicesAccordion = ({ services = [] }) => {
  const [openId, setOpenId] = useState(null);
  const reduceMotion = useReducedMotion();

  const toggle = (id) => setOpenId((cur) => (cur === id ? null : id));

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 items-start">
      {services.map((svc) => (
        <ServiceCard
          key={svc.id}
          svc={svc}
          isOpen={openId === svc.id}
          onToggle={toggle}
          reduceMotion={reduceMotion}
        />
      ))}
    </div>
  );
};

export default ServicesAccordion;
